/* here&found — <hf-nav> (assets/hf-nav.js).
   Progressive enhancement for the header (sections/header.liquid). The
   baseline needs NO JS and is not touched: mega panels open on CSS
   :hover/:focus-within, the mobile drawer and its groups are native
   <details> (Help collapsed by default), and a tiny inline script drives the
   scrolled logo glide. This element layers on what CSS cannot express:
   - ARIA state: aria-haspopup/aria-expanded on the four nav links, kept in
     sync with hover-, focus- and click-opened panels;
   - touch: on hover-less devices the first tap on a nav link opens its panel
     (instead of navigating), the second tap follows the link;
   - keyboard: Escape closes any open panel or the mobile drawer and returns
     focus to its trigger;
   - pointer hygiene: clicking outside closes click-opened panels and the
     drawer; growing the viewport past the desktop breakpoint closes the
     drawer so it can't reopen overlapping the desktop nav.
   Panels opened by click get .is-open (styled in header.liquid alongside the
   :hover/:focus-within rules). All listeners are removed on disconnect. */

class HFNav extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.hf-hdr__navitem')).filter((item) => item.querySelector('.hf-mega'));
    this.drawer = this.querySelector('.hf-hdr__drawer');
    this.hoverless = window.matchMedia('(hover: none)');
    this.desktop = window.matchMedia('(min-width: 1000px)');

    this.items.forEach((item) => {
      const link = item.querySelector('.hf-navlink');
      if (!link) return;
      link.setAttribute('aria-haspopup', 'true');
      link.setAttribute('aria-expanded', 'false');
    });

    this.onClick = (event) => {
      const link = event.target.closest('.hf-navlink');
      if (!link || !this.contains(link)) return;
      const item = link.closest('.hf-hdr__navitem');
      if (!item || !item.querySelector('.hf-mega')) return;
      if (this.hoverless.matches && !item.classList.contains('is-open')) {
        event.preventDefault();
        this.open(item);
      }
    };

    this.onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      const open = this.items.find((item) => item.classList.contains('is-open') || item.matches(':focus-within'));
      if (open) {
        this.close(open);
        const link = open.querySelector('.hf-navlink');
        if (link) link.focus();
        return;
      }
      if (this.drawer && this.drawer.open) {
        this.drawer.open = false;
        const summary = this.drawer.querySelector('summary');
        if (summary) summary.focus();
      }
    };

    // mirror :hover/:focus-within into aria-expanded
    this.onFocusOrHover = (event) => {
      const item = event.target.closest && event.target.closest('.hf-hdr__navitem');
      this.items.forEach((candidate) => {
        const link = candidate.querySelector('.hf-navlink');
        if (!link) return;
        const expanded = candidate === item || candidate.classList.contains('is-open') || candidate.matches(':focus-within');
        link.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    };

    this.onDocClick = (event) => {
      if (this.contains(event.target)) return;
      this.items.forEach((item) => this.close(item));
      if (this.drawer && this.drawer.open) this.drawer.open = false;
    };

    this.onDesktopChange = (event) => {
      if (event.matches && this.drawer && this.drawer.open) this.drawer.open = false;
    };

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('focusin', this.onFocusOrHover);
    this.addEventListener('mouseover', this.onFocusOrHover);
    document.addEventListener('click', this.onDocClick);
    this.desktop.addEventListener('change', this.onDesktopChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('focusin', this.onFocusOrHover);
    this.removeEventListener('mouseover', this.onFocusOrHover);
    document.removeEventListener('click', this.onDocClick);
    this.desktop.removeEventListener('change', this.onDesktopChange);
  }

  open(item) {
    this.items.forEach((candidate) => {
      if (candidate !== item) this.close(candidate);
    });
    item.classList.add('is-open');
    const link = item.querySelector('.hf-navlink');
    if (link) link.setAttribute('aria-expanded', 'true');
  }

  close(item) {
    item.classList.remove('is-open');
    const link = item.querySelector('.hf-navlink');
    if (link) link.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('hf-nav')) customElements.define('hf-nav', HFNav);
